import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ADS_BATCH_STATES } from '@wit-support/admin-contracts';
import { Badge, Button, Card, EmptyState, ErrorCard, LoadingTableCard, Pagination, Select, Table, TextLink, formatCount, SearchableSelect, useTableColumns, } from '@wit-support/ui';
import { adsBatchStateLabelRu, AdsBatchStateBadge } from '@wit-support/ui-domain';
import { AdsTime } from '../components/ads-time.js';
import { CircleCheck, ListChecks, SquarePlus } from 'lucide-react';
import { listBatches } from '../api.js';
import { ADS_QUERY_DEFAULTS, batchesListPollMs } from '../query-policy.js';
import { useAdsLauncherPerms } from '../perms.js';
import { AdsScreen, BATCHES_KEY, describeAdsError, useAccounts, useAdsHealth } from '../shared.js';
import { MoneyByCurrency } from '../components/money.js';
import { ColumnHeader, TableColumnsBar, adsTableColumnsKey, loadingHeaderCells, } from '../components/table-kit.js';
/**
 * Колонки списка пачек. Сортировки по колонкам нет: список курсорный
 * (страница за страницей с сервера), а сервер сортирует только по дате —
 * сортировать на клиенте одну страницу из многих было бы неправдой.
 */
/** Колонки старого auto-ads (без `features.builderV4`): без v4-колонки «Структура». */
const V4_ONLY = new Set(['structure']);
const COLUMNS = [
    { id: 'date', label: 'Дата', hideable: false, defaultPin: 'left' },
    { id: 'template', label: 'Шаблон' },
    { id: 'accounts', label: 'Кабинеты' },
    { id: 'ads', label: 'Объявлений' },
    { id: 'campaigns', label: 'Кампаний' },
    // v4 §11.4: формы кампаний пачки («1-2-1»); пачка до v4 — «—»
    { id: 'structure', label: 'Структура' },
    { id: 'state', label: 'Статус' },
    { id: 'verify', label: 'Сверка с Meta' },
    { id: 'budget', label: 'Бюджет в день' },
    { id: 'author', label: 'Автор' },
];
const COLUMNS_OLD = COLUMNS.filter((c) => !V4_ONLY.has(c.id));
const HINTS = {
    structure: 'Сколько кампаний, групп объявлений и объявлений в каждой кампании пачки: «1-2-1» — 1 кампания, 2 группы объявлений, по 1 объявлению.',
    verify: 'Совпадает ли то, что создано в Meta, с задуманным в шаблоне. Если нет — сколько запусков с расхождениями или требуют внимания.',
};
const PAGE_SIZES = [25, 50, 100];
const ALL = '__all__';
/** «1 запуск ждёт включения — старт прошёл» (список пачек и шапка пачки, v4 §11.4). */
export function lateWaitingText(n) {
    return `${formatCount(n, ['запуск ждёт', 'запуска ждут', 'запусков ждут'])} включения — старт прошёл`;
}
/**
 * Опоздавшие к старту (v4 §11.4) — отдельной частью рядом со сверкой (раньше
 * `ready_paused` считался «✓»). Нет `lateStart` (старый auto-ads) или
 * опоздавших — null.
 */
export function lateSummary(b) {
    const n = b.lateStart?.launches ?? 0;
    return n > 0 ? lateWaitingText(n) : null;
}
/** «1-2-1», «1-2-1, 1-3-1» — формы кампаний пачки; пачка до v4 — null. */
export function structureText(b) {
    const shapes = b.structure?.shapes ?? [];
    return shapes.length > 0 ? shapes.join(', ') : null;
}
/**
 * Сводка сверки пачки по счётчикам статусов: сколько запусков требуют
 * человека — живые с подменой после старта или с неудавшейся паузой
 * (`needs_attention`, `pause_failed`) — и сколько с расхождениями
 * (`blocked`, `paused_on_drift`).
 */
export function verifySummary(b) {
    const attention = (b.counts.needs_attention ?? 0) + (b.counts.pause_failed ?? 0);
    const bad = (b.counts.blocked ?? 0) + (b.counts.paused_on_drift ?? 0);
    const parts = [];
    if (attention > 0) {
        parts.push(`${formatCount(attention, ['запуск требует', 'запуска требуют', 'запусков требуют'])} внимания`);
    }
    if (bad > 0)
        parts.push(`${formatCount(bad, ['запуск', 'запуска', 'запусков'])} с расхождениями`);
    if (parts.length > 0)
        return { ok: false, text: parts.join(', ') };
    const checked = (b.counts.waiting_start ?? 0) +
        (b.counts.running ?? 0) +
        (b.counts.paused ?? 0) +
        (b.counts.ready_paused ?? 0);
    return checked > 0 ? { ok: true, text: '✓' } : { ok: true, text: VERIFY_NONE_TEXT };
}
/** Сверка прошла без расхождений — подписью, а не голой галочкой. */
export const VERIFY_CLEAN_TEXT = 'без расхождений';
/** Ни один запуск пачки ещё не сверялся с Meta (не созданы, отменены, удалены). */
export const VERIFY_NONE_TEXT = 'не проверялась';
/** «Bid cap без ставки — весь мир · v1»: версия отделена от названия. */
export function templateLabel(b) {
    return `${b.templateName} · v${b.templateVersion}`;
}
export function BatchesPage() {
    const { canLaunch } = useAdsLauncherPerms();
    return (_jsx(AdsScreen, { title: "\u0417\u0430\u043F\u0443\u0441\u043A\u0438", description: "\u041F\u0430\u0447\u043A\u0438 \u0437\u0430\u043F\u0443\u0441\u043A\u043E\u0432: \u0447\u0442\u043E \u0441\u043E\u0437\u0434\u0430\u043D\u043E, \u0432 \u043A\u0430\u043A\u043E\u043C \u0441\u043E\u0441\u0442\u043E\u044F\u043D\u0438\u0438 \u0438 \u043F\u0440\u043E\u0448\u043B\u0430 \u043B\u0438 \u0441\u0432\u0435\u0440\u043A\u0430 \u0441 Meta.", width: "full", toolbar: canLaunch
            ? [
                {
                    label: 'Новый запуск',
                    priority: 'primary',
                    render: (size) => (_jsx(Button, { asChild: true, size: size, className: "gap-1.5", children: _jsxs(Link, { to: "/fb-launches/new", children: [_jsx(SquarePlus, { "aria-hidden": true, className: "h-4 w-4" }), "\u041D\u043E\u0432\u044B\u0439 \u0437\u0430\u043F\u0443\u0441\u043A"] }) })),
                },
            ]
            : [], children: _jsx(BatchesBody, {}) }));
}
function BatchesBody() {
    const accounts = useAccounts();
    const [state, setState] = useState(undefined);
    const [account, setAccount] = useState(undefined);
    const [pageSize, setPageSize] = useState(25);
    // Курсорная пагинация: курсор каждой открытой страницы (первая — без курсора).
    const [cursors, setCursors] = useState([undefined]);
    const [page, setPage] = useState(1);
    const reset = () => {
        setCursors([undefined]);
        setPage(1);
    };
    const q = useQuery({
        ...ADS_QUERY_DEFAULTS,
        queryKey: [...BATCHES_KEY, { state, account, pageSize, cursor: cursors[page - 1] }],
        queryFn: () => listBatches({
            ...(state ? { state } : {}),
            ...(account ? { platformAccountId: account } : {}),
            limit: pageSize,
            ...(cursors[page - 1] ? { cursor: cursors[page - 1] } : {}),
        }),
        placeholderData: keepPreviousData,
        // в работе — 15 с, есть ждущие старта — 60 с, иначе без опроса
        refetchInterval: (query) => batchesListPollMs(query.state.data?.items),
    });
    const names = new Map((accounts.data?.items ?? []).map((a) => [a.platformAccountId, a.name]));
    const health = useAdsHealth();
    const v4 = health.data?.features?.builderV4 === true;
    const { canActivate } = useAdsLauncherPerms();
    const cols = useTableColumns(adsTableColumnsKey('batches'), v4 ? COLUMNS : COLUMNS_OLD);
    return (_jsxs("div", { className: "flex flex-col gap-4", children: [_jsxs(TableColumnsBar, { columns: cols, children: [_jsxs(Select, { value: state ?? ALL, onValueChange: (v) => {
                            setState(v === ALL ? undefined : v);
                            reset();
                        }, children: [_jsx(Select.Trigger, { "aria-label": "\u0421\u0442\u0430\u0442\u0443\u0441", className: "w-56", children: _jsx(Select.Value, {}) }), _jsxs(Select.Content, { children: [_jsx(Select.Item, { value: ALL, children: "\u0412\u0441\u0435 \u0441\u0442\u0430\u0442\u0443\u0441\u044B" }), ADS_BATCH_STATES.map((s) => (_jsx(Select.Item, { value: s, children: adsBatchStateLabelRu(s) }, s)))] })] }), _jsx(SearchableSelect, { "aria-label": "\u041A\u0430\u0431\u0438\u043D\u0435\u0442", className: "w-56", value: account ?? ALL, onChange: (v) => {
                            setAccount(v === ALL ? undefined : v);
                            reset();
                        }, options: [
                            { value: ALL, label: 'Все кабинеты' },
                            ...(accounts.data?.items ?? []).map((a) => ({
                                value: a.platformAccountId,
                                label: a.name,
                                keywords: [a.platformAccountId],
                            })),
                        ] })] }), q.isError ? (_jsx(ErrorCard, { children: describeAdsError(q.error) })) : q.isLoading || !q.data ? (_jsx(LoadingTableCard, { headerCells: loadingHeaderCells(cols.visible), rows: 5, density: "compact" })) : q.data.items.length === 0 && page === 1 ? (_jsx(Card, { children: _jsx(EmptyState, { icon: ListChecks, title: "\u0417\u0430\u043F\u0443\u0441\u043A\u043E\u0432 \u043F\u043E\u043A\u0430 \u043D\u0435\u0442", description: state || account ? 'Под фильтр ничего не попало.' : 'Создайте первый запуск.' }) })) : (_jsx(Card, { className: "overflow-hidden", children: _jsxs(Table, { density: "compact", children: [_jsx(Table.Header, { children: _jsx(Table.Row, { children: cols.visible.map((c) => (_jsx(ColumnHeader, { column: c, ...(HINTS[c.id] ? { hint: HINTS[c.id] } : {}) }, c.id))) }) }), _jsx(Table.Body, { children: q.data.items.map((b) => (_jsx(Table.Row, { "data-testid": `batch-${b.id}`, children: cols.visible.map((c) => (_jsx(BatchCell, { column: c, batch: b, names: names, canActivate: canActivate }, c.id))) }, b.id))) })] }) })), q.data && !(q.data.items.length === 0 && page === 1) ? (_jsx(Pagination, { page: page, pageSize: pageSize, 
                // курсорная лента: на последней странице итог известен — «Показано 1–3 из 3», а не «1–25»
                total: q.data.nextCursor == null ? (page - 1) * pageSize + q.data.items.length : null, pageSizeOptions: PAGE_SIZES, hasNextPage: q.data.nextCursor != null, isFetching: q.isFetching, onPageChange: (p) => {
                    if (p > page && q.data?.nextCursor) {
                        setCursors((cur) => {
                            const next = cur.slice(0, page);
                            next[page] = q.data.nextCursor;
                            return next;
                        });
                    }
                    if (p <= cursors.length || p === page + 1)
                        setPage(p);
                }, onPageSizeChange: (s) => {
                    setPageSize(s);
                    reset();
                } })) : null] }));
}
function BatchCell({ column: c, batch: b, names, canActivate, }) {
    switch (c.id) {
        case 'date':
            return (_jsx(Table.Cell, { column: c, children: _jsx(TextLink, { asChild: true, children: _jsx(Link, { to: "/fb-launches/batches/$id", params: { id: b.id }, children: _jsx(AdsTime, { iso: b.createdAt, preset: "dateTimeNumeric" }) }) }) }));
        case 'template':
            return _jsx(Table.Cell, { column: c, children: templateLabel(b) });
        case 'accounts':
            return (_jsx(Table.Cell, { column: c, children: b.platformAccountIds.map((id) => names.get(id) ?? id).join(', ') }));
        case 'ads':
            return (_jsx(Table.Cell, { column: c, className: "tabular-nums", children: b.totals.ads }));
        case 'campaigns':
            return (_jsx(Table.Cell, { column: c, className: "tabular-nums", children: b.totals.campaigns }));
        case 'structure': {
            const text = structureText(b);
            return (_jsx(Table.Cell, { column: c, className: "whitespace-nowrap tabular-nums", children: text ?? _jsx("span", { className: "text-[var(--color-text-muted)]", children: "\u2014" }) }));
        }
        case 'state':
            return (_jsx(Table.Cell, { column: c, children: _jsxs("div", { className: "flex flex-wrap items-center gap-1", children: [_jsx(AdsBatchStateBadge, { state: b.state }), b.mode === 'draft' ? _jsx(Badge, { children: "\u0447\u0435\u0440\u043D\u043E\u0432\u0438\u043A" }) : null] }) }));
        case 'verify': {
            const v = verifySummary(b);
            const late = lateSummary(b);
            return (_jsx(Table.Cell, { column: c, "data-testid": `batch-verify-${b.id}`, children: _jsxs("div", { className: "flex flex-col items-start gap-1", children: [v.ok ? (v.text === '✓' ? (_jsxs("span", { className: "inline-flex items-center gap-1 whitespace-nowrap text-xs", children: [_jsx(CircleCheck, { "aria-hidden": true, className: "h-4 w-4 text-[var(--color-state-success-fg)]" }), VERIFY_CLEAN_TEXT] })) : (_jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: v.text }))) : (_jsx(Badge, { variant: "danger", children: v.text })), late ? (
                        // «требует внимания» из-за опоздавших — с действием: на пачку, где «Включить сейчас…»
                        _jsxs("span", { className: "inline-flex flex-wrap items-center gap-1", "data-testid": `batch-late-${b.id}`, children: [_jsx(Badge, { variant: "warning", children: late }), canActivate ? (_jsx(TextLink, { asChild: true, className: "text-xs", children: _jsx(Link, { to: "/fb-launches/batches/$id", params: { id: b.id }, children: "\u0412\u043A\u043B\u044E\u0447\u0438\u0442\u044C\u2026" }) })) : null] })) : null] }) }));
        }
        case 'budget':
            return (_jsx(Table.Cell, { column: c, children: _jsx(MoneyByCurrency, { value: b.totals.dailyBudgetMinorByCurrency }) }));
        case 'author':
            return (_jsx(Table.Cell, { column: c, className: "text-xs", children: _jsxs("div", { className: "flex flex-wrap items-center gap-1", children: [_jsx("span", { className: "[overflow-wrap:anywhere]", children: b.createdBy ?? '—' }), b.source === 'agent' ? (_jsx(Badge, { variant: "info", "data-testid": "batch-source-agent", children: "\u043D\u0435\u0439\u0440\u043E\u043D\u043A\u0430" })) : null] }) }));
    }
}
//# sourceMappingURL=batches.js.map